// The documents and templates of the weekly price list. The values are the names the price
// list service accepts (PriceListDocuments on the server).

// The manage.rapnet permission of the price list screens (Prices > Price List and the Rapaport Trade lists), which
// the BFF checks too. RAPI only needs the user to sign in.
export const PRICE_LIST_PERMISSION = 'pricelist_upload';

// The price list screens to look at only: the pages below open, their PDFs download, and every upload, save, rate,
// publish and delete is hidden; the BFF refuses those too. The Excel and parcels uploads, the weekly texts and the
// templates are editors only, so they stay with pricelist_upload.
export const PRICE_LIST_READONLY_PERMISSION = 'pricelist_readonly';
export const PRICE_LIST_VIEW_PERMISSIONS = [PRICE_LIST_PERMISSION, PRICE_LIST_READONLY_PERMISSION];
const PRICE_LIST_VIEW_ROUTES = [
  '/pricelist/currencyrates',
  '/pricelist/preview',
  '/pricelist/publish',
  '/pricelist/rapnetlists',
  '/rapaporttradepricelist/preview',
  '/rapaporttradepricelist/publish'
];
// The permission a price list page needs, for its route and its menu link.
export const priceListRoutePermission = (route: string) =>
  PRICE_LIST_VIEW_ROUTES.includes(route) ? PRICE_LIST_VIEW_PERMISSIONS : PRICE_LIST_PERMISSION;

export const PRICE_LIST_DOCUMENT_OPTIONS = [
  { label: 'Round', value: 'Round' },
  { label: 'Pear', value: 'Pear' },
  { label: 'Round Rupee', value: 'Round_Rupee' },
  { label: 'Pear Rupee', value: 'Pear_Rupee' },
  { label: 'Parcels', value: 'Parcels' },
  { label: 'Round, Pear and Parcels', value: 'All' },
  { label: 'Round and Pear', value: 'RapaportPriceSheet' }
];

// The shape and currency of each document's parts, for the warnings shown above its preview.
// The parcels part is the parcels price list the week carries.
export const PRICE_LIST_DOCUMENT_PARTS: Record<
  string,
  Array<{ shapeId: number; currencyId: number }>
> = {
  Round: [{ shapeId: 1, currencyId: 1 }],
  Pear: [{ shapeId: 2, currencyId: 1 }],
  Round_Rupee: [{ shapeId: 1, currencyId: 3 }],
  Pear_Rupee: [{ shapeId: 2, currencyId: 3 }],
  Parcels: [],
  All: [
    { shapeId: 1, currencyId: 1 },
    { shapeId: 2, currencyId: 1 }
  ],
  RapaportPriceSheet: [
    { shapeId: 1, currencyId: 1 },
    { shapeId: 2, currencyId: 1 }
  ]
};

export const PRICE_LIST_PARCELS_DOCUMENTS = ['Parcels', 'All'];

export const PRICE_LIST_TEMPLATE_OPTIONS = [
  { label: 'US Round', value: 'US-Round' },
  { label: 'US Pear', value: 'US-Pear' },
  { label: 'Rupee Round', value: 'Rupee-Round' },
  { label: 'Rupee Pear', value: 'Rupee-Pear' },
  { label: 'Parcels', value: 'Parcels' }
];

// The news boxes whose length the server measures (PriceListTextFit.field), by the text that fills them, with the
// titles that can replace their bold prefix (PriceListNews in Share_Pricelist) and the prefix of the rupee lists.
export const PRICE_LIST_NEWS = [
  {
    key: 'roundNews',
    titleKey: 'roundNewsTitle',
    rupeeTitleKey: 'roundNewsRupeeTitle',
    field: 'RoundNews',
    label: 'Round news',
    rupeePrefix: 'Round News:'
  },
  {
    key: 'pearNews',
    titleKey: 'pearNewsTitle',
    rupeeTitleKey: 'pearNewsRupeeTitle',
    field: 'PearNews',
    label: 'Pear news',
    rupeePrefix: 'Pear News:'
  }
] as const;

export const PRICE_LIST_TEXT_FIT_UNAVAILABLE =
  'The length of the texts cannot be checked right now.';

// The news texts (PriceListNews in Share_Pricelist): a title printed in bold instead of the usual one, and bold text.
// The service keeps the first 300 characters of a title (the column is nvarchar(300)).
export const PRICE_LIST_NEWS_TITLE_NOTE =
  'For this week only. Change a title, or delete it to print the news with no title.';
export const PRICE_LIST_NEWS_TITLE_MAX_LENGTH = 300;
export const PRICE_LIST_NEWS_BOLD_NOTE =
  'Select words and press B (or Ctrl+B) to print them in bold.';

// The bold prefix of a news on the US price lists; the rupee ones print the rupeePrefix above.
export const PRICE_LIST_NEWS_PREFIX = 'News:';

// Under the "Publish now" button: when the service accepts it (PriceListManager.EnsurePublishNowAllowed in
// Share_Pricelist).
export const PRICE_LIST_PUBLISH_NOW_NOTE =
  'In production, "Publish now" works only once the week is live: from Thursday 23:55 New York time to the ' +
  "end of Friday, to correct the published price list. Before that it would put next week's prices on the " +
  "site early and notify the subscribers of last week's list. On dev and staging it works at any time, for tests.";

// How the service's refusal (409) of an Excel upload starts when the week's automatic publish is turned off
// (PriceUploadManager.StartExcelUploadAsync in Share_Pricelist): the screen then asks to confirm the upload.
export const PRICE_LIST_AUTOMATIC_OFF_MESSAGE = 'Automatic publish of the';

// The Rapaport Trade price list (Prices > Rapaport Trade): its template, and its weekly text by the field the server
// measures (PriceListTextFit.field): the market comment, a paragraph per line that prints the bold the editor shows
// (RapaportTradeText in Share_Pricelist). The note of page 1 is part of the template.
export const RAPAPORT_TRADE_PRICE_LIST_TEMPLATE_OPTIONS = [
  { label: 'Rapaport Trade', value: 'RapaportTrade' }
];
export const RAPAPORT_TRADE_PRICE_LIST_COMMENT = {
  field: 'MarketComment',
  label: 'Weekly market comment (page 2)'
} as const;
export const RAPAPORT_TRADE_PRICE_LIST_COMMENT_NOTE =
  'Paste the market comment email as it is: each line is a paragraph, and the bold prints as it shows.';
// The list dates offered: the next list's and the ones of the weeks before it.
export const RAPAPORT_TRADE_PRICE_LIST_EARLIER_WEEKS = 12;

// A publish or an Excel upload is followed through its status, read every 3 s. 10 failed reads in a row, or
// 15 minutes, stop following it: an upload or a "Publish now" ends well before. The service takes an upload that
// still says Running after 15 minutes as stale, and a publish after 30 (a scheduled one may wait for the DB job).
export const PRICE_LIST_STATUS_POLLING = {
  intervalMs: 3000,
  maxFailures: 10,
  maxDurationMs: 15 * 60 * 1000
};
